import { useCallback, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { anchorRef, bindTrigger } from 'material-ui-popup-state/hooks';

import {
  IconButton,
  SearchForm,
  TableContainer,
  TableHeader,
  FilterListIcon,
  snackbar,
  Typography,
} from '@occc-app/design-system';
import {
  usePaginationParams,
  BEFORE_PARAM,
  AFTER_PARAM,
} from '@occc-app/hooks/usePaginationParams';
import { useSearchParams, SEARCH_PARAM } from '@occc-app/hooks/useSearchParams';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';
import {
  RetailerNetworkLocationDataFragment,
  Retailer_Network_User_Role,
  useRemoveUserFromRetailerNetworkMutation,
  useAssociateLocationsWithRetailerNetworkUsersMutation,
  useDisassociateLocationsWithRetailerNetworkUsersMutation,
  RetailerNetworkUserUpdateMutation,
  RetailerNetworkUserUpdate,
  User_Role,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  CreateRetailerNetworkUser,
  UpdateRetailerNetworkLocations,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { LocationUsersTable } from './location-users-table.component';

import {
  useRetailerNetworkUserCreateMutation,
  useRetailerNetworkUserUpdateMutation,
} from '../../../../data-access/mutations';
import { useGetRetailerNetworkUsers } from '../../../../data-access/queries';
import { useUserRoleFilter } from '../../../components/user-role-filter/use-user-role-filter.hook';
import { UserRoleFilter } from '../../../components/user-role-filter/user-role-filter.component';
import { AssociateUsersWithLocationsDialog } from '../../../components/associate-users-with-locations-dialog/associate-users-with-locations-dialog.component';
import { AssociateUsersWithLocationsFormValues } from '../../../components/associate-users-with-locations-dialog/associate-users-with-locations-dialog.types';
import { CreateAndAssociateNewUserWithLocationsFormValues } from '../../../components/create-and-associate-new-user-with-locations-dialog/create-and-associate-new-user-with-locations-dialog.types';
import { CreateAndAssociateNewUserWithLocationsDialog } from '../../../components/create-and-associate-new-user-with-locations-dialog/create-and-associate-new-user-with-locations-dialog.component';
import { RemoveUserFromLocationDialog } from '../../../components/remove-user-from-location-dialog.component';
import { RemoveUserFromRetailerNetworkDialog } from '../../../components/remove-user-from-retailer-network-dialog.component';
import { routeCreator } from '../../../retailer-connect.utils';

type LocationUsersTableContainerProps = {
  retailerNetworkUuid: string;
  location: RetailerNetworkLocationDataFragment;
  title?: string;
  searchParamName?: string;
  userRoleFilterParamName?: string;
  beforeParamName?: string;
  afterParamName?: string;
};

export const LocationUsersTableContainer = ({
  retailerNetworkUuid,
  location,
  title = 'Admins and Users',
  searchParamName,
  userRoleFilterParamName,
  beforeParamName,
  afterParamName,
}: LocationUsersTableContainerProps) => {
  const navigate = useNavigate();
  const showMobilePhone = useIsUserPermittedTo(Feature.RETAILER_NETWORK_USER_MOBILE_PHONE_VIEW);
  const [search, setSearch] = useSearchParams({
    searchParamName: searchParamName ?? SEARCH_PARAM,
    beforeParamName: beforeParamName ?? BEFORE_PARAM,
    afterParamName: afterParamName ?? AFTER_PARAM,
  });
  const [paginationInfo, onPageChange] = usePaginationParams({ beforeParamName, afterParamName });
  const { userRoleFilter, userFilterPopupState, userFilterPopoverProps, setUserRoleFilter } =
    useUserRoleFilter({
      filterParamName: userRoleFilterParamName,
      beforeParamName,
      afterParamName,
    });

  const [removeUserFromLocationDialogOpen, setRemoveUserFromLocationDialogOpen] =
    useState<boolean>(false);
  const [removeUserFromRetailerNetworkDialogOpen, setRemoveUserFromRetailerNetworkDialogOpen] =
    useState<boolean>(false);
  const [userUuidToRemove, setUserUuidToRemove] = useState<string | null>(null);
  const [removeUserFromLocation, { loading: isRemoveUserFromLocationInProgress }] =
    useDisassociateLocationsWithRetailerNetworkUsersMutation({
      onCompleted: () => snackbar.success('User has been successfully removed from location'),
      refetchQueries: ['retailerNetworkUsers'],
    });

  const [removeUserFromRetailerNetwork, { loading: isRemoveUserFromRetailerNetworkInProgress }] =
    useRemoveUserFromRetailerNetworkMutation({
      onCompleted: () =>
        snackbar.success('User has been successfully removed from retailer network'),
      refetchQueries: ['retailerNetworkUsers'],
    });

  const onRemoveUserFromLocationDialogConfirm = useCallback(async () => {
    if (userUuidToRemove) {
      await removeUserFromLocation({
        variables: {
          retailerNetworkUuid,
          locationUuids: [location.uuid],
          retailerNetworkUserUuids: [userUuidToRemove],
        },
      });
      setUserUuidToRemove(null);
      setRemoveUserFromLocationDialogOpen(false);
    }
  }, [location.uuid, retailerNetworkUuid, removeUserFromLocation, userUuidToRemove]);

  const onRemoveUserFromRetailerNetwork = useCallback(async () => {
    if (userUuidToRemove) {
      await removeUserFromRetailerNetwork({
        variables: {
          retailerNetworkUuid,
          userUuid: userUuidToRemove,
        },
      });

      setUserUuidToRemove(null);
      setRemoveUserFromRetailerNetworkDialogOpen(false);
    }
  }, [removeUserFromRetailerNetwork, retailerNetworkUuid, userUuidToRemove]);

  const handleRemoveUserFromLocation = (userUuid: string) => {
    setRemoveUserFromLocationDialogOpen(true);
    setUserUuidToRemove(userUuid);
  };

  const handleRemoveUserFromRetailerNetwork = (openCommerceUserUuid: string) => {
    setRemoveUserFromRetailerNetworkDialogOpen(true);
    setUserUuidToRemove(openCommerceUserUuid);
  };

  const [
    isAssociateExistingUsersWithLocationsDialogOpen,
    setAssociateExistingUsersWithLocationsDialogOpen,
  ] = useState<boolean>(false);
  const handleAssociateExistingUsersWithLocationsDialogOpen = useCallback(() => {
    setAssociateExistingUsersWithLocationsDialogOpen(
      !isAssociateExistingUsersWithLocationsDialogOpen,
    );
  }, [
    setAssociateExistingUsersWithLocationsDialogOpen,
    isAssociateExistingUsersWithLocationsDialogOpen,
  ]);
  const [
    associateLocationsWithRetailerNetworkUsers,
    { loading: associateLocationsWithRetailerNetworkUsersInProgress },
  ] = useAssociateLocationsWithRetailerNetworkUsersMutation({
    onCompleted: () => snackbar.success('Users have been successfully associated with location'),
    refetchQueries: ['retailerNetworkUsers'],
  });
  const onAssociateLocationsWithRetailerNetworkUsersDialogConfirm = useCallback(
    async ({ locations, users }: AssociateUsersWithLocationsFormValues) => {
      const locationUuids = locations.map((location) => location.uuid);
      const retailerNetworkUserUuids = users.map((user) => user.uuid);

      await associateLocationsWithRetailerNetworkUsers({
        variables: {
          retailerNetworkUuid,
          locationUuids,
          retailerNetworkUserUuids,
        },
      });

      setAssociateExistingUsersWithLocationsDialogOpen(false);
    },
    [retailerNetworkUuid, associateLocationsWithRetailerNetworkUsers],
  );

  const [isAssociateNewUserWithLocationsDialogOpen, setAssociateNewUserWithLocationsOpen] =
    useState<boolean>(false);
  const handleAssociateNewUserWithLocationsDialogOpen = useCallback(() => {
    setAssociateNewUserWithLocationsOpen(!isAssociateNewUserWithLocationsDialogOpen);
  }, [setAssociateNewUserWithLocationsOpen, isAssociateNewUserWithLocationsDialogOpen]);

  const [retailerNetworkUserCreateMutation] = useRetailerNetworkUserCreateMutation();

  const handleCreateNewUserAndAssociateWithLocation = async (
    values: CreateAndAssociateNewUserWithLocationsFormValues,
  ) => {
    const locationUuids = values.locations.map((location) => location.uuid);

    await retailerNetworkUserCreateMutation({
      variables: {
        input: {
          locationUuids,
          openCommerceUser: {
            name: {
              first: values.firstName,
              last: values.lastName,
            },
            email: values.email.trim(),
            userRole: User_Role.RcUser,
            phoneNumber: values.phoneNumber ? values.phoneNumber.trim() : undefined,
          },
          retailerNetworkUuid,
          role: values.userRole,
        },
      },
      onCompleted: () =>
        snackbar.success('User has been successfully created and associated with location(s)'),
      refetchQueries: ['retailerNetworkUsers'],
    });

    handleAssociateNewUserWithLocationsDialogOpen();
  };

  const handleRedirectToUserDetailsPage = useCallback(
    (userUuid: string) => {
      navigate(routeCreator.userDetails(retailerNetworkUuid, userUuid));
    },
    [navigate, retailerNetworkUuid],
  );

  const onRetailerNetworkUserUpdateCompleted = (
    retailerNetworkUserUpdateData: RetailerNetworkUserUpdateMutation,
  ) => {
    const user = retailerNetworkUserUpdateData.retailerNetworkUserUpdate;
    snackbar.success(`${user.name} user role has been successfully changed`);
  };

  const [retailerNetworkUserUpdate, { loading: isRetailerNetworkUserUpdateInProgress }] =
    useRetailerNetworkUserUpdateMutation({
      refetchQueries: ['retailerNetworkUsers'],
      onCompleted: onRetailerNetworkUserUpdateCompleted,
    });

  const handleUpdateRetailerNetworkUserRole = async (input: RetailerNetworkUserUpdate) => {
    await retailerNetworkUserUpdate({
      variables: {
        input,
      },
    });
  };

  const { loading, error, data, paginationProps } = useGetRetailerNetworkUsers({
    filter: {
      retailerNetworkUuidEquals: retailerNetworkUuid,
      locationUuidEquals: location.uuid,
      roleIn:
        userRoleFilter && userRoleFilter !== 'all'
          ? [userRoleFilter]
          : [Retailer_Network_User_Role.SiteAdmin, Retailer_Network_User_Role.SiteUser],
      nameLike: search,
    },
    paginationInfo,
    onPageChange,
  });

  const tableActions = useMemo(() => {
    return (
      <Box display='flex' justifyContent='end' alignItems='center' width='100%'>
        <SearchForm
          key='search'
          onSubmit={(values) => {
            setSearch(values.search);
          }}
          initialValues={{ search }}
        />
        <IconButton
          {...bindTrigger(userFilterPopupState)}
          ref={anchorRef(userFilterPopupState)}
          sx={{ ml: 1 }}
        >
          <FilterListIcon fontSize='inherit' />
        </IconButton>
        <Can I={UpdateRetailerNetworkLocations({ uuid: retailerNetworkUuid })}>
          <Can I={CreateRetailerNetworkUser()}>
            <Button
              key='associateNewUsersButton'
              color='primary'
              sx={{ minWidth: 'fit-content', ml: 2 }}
              variant='contained'
              onClick={handleAssociateNewUserWithLocationsDialogOpen}
              role='button'
            >
              Add New
            </Button>
          </Can>
          <Button
            key='associateExistingUsersButton'
            color='primary'
            sx={{ minWidth: 'fit-content', ml: 2 }}
            variant='contained'
            onClick={handleAssociateExistingUsersWithLocationsDialogOpen}
            role='button'
          >
            Add Existing
          </Button>
        </Can>
      </Box>
    );
  }, [
    search,
    setSearch,
    userFilterPopupState,
    retailerNetworkUuid,
    handleAssociateExistingUsersWithLocationsDialogOpen,
    handleAssociateNewUserWithLocationsDialogOpen,
  ]);

  return (
    <>
      <TableContainer>
        <TableHeader
          title={
            <Typography variant='subtitle1' component='h2'>
              {title}
            </Typography>
          }
          disableTitleTypography
          actions={tableActions}
        />
        <LocationUsersTable
          data={data}
          loading={loading}
          error={error}
          isSearching={!!search}
          isPhoneNumberVisible={showMobilePhone}
          paginationProps={paginationProps}
          handleRedirectToUserDetailsPage={handleRedirectToUserDetailsPage}
          handleRemoveUserFromLocation={handleRemoveUserFromLocation}
          handleRemoveUserFromRetailerNetwork={handleRemoveUserFromRetailerNetwork}
          handleUpdateRetailerNetworkUserRole={handleUpdateRetailerNetworkUserRole}
          isRetailerNetworkUserUpdateInProgress={isRetailerNetworkUserUpdateInProgress}
        />
      </TableContainer>

      <UserRoleFilter
        userRoleFilter={userRoleFilter}
        userFilterPopoverProps={userFilterPopoverProps}
        setUserRoleFilter={setUserRoleFilter}
      />

      <RemoveUserFromLocationDialog
        open={removeUserFromLocationDialogOpen}
        setOpen={setRemoveUserFromLocationDialogOpen}
        onConfirm={onRemoveUserFromLocationDialogConfirm}
        isRemoveUserFromLocationInProgress={isRemoveUserFromLocationInProgress}
      />

      <RemoveUserFromRetailerNetworkDialog
        open={removeUserFromRetailerNetworkDialogOpen}
        setOpen={setRemoveUserFromRetailerNetworkDialogOpen}
        onConfirm={onRemoveUserFromRetailerNetwork}
        isRemoveUserFromRetailerNetworkInProgress={isRemoveUserFromRetailerNetworkInProgress}
      />

      <AssociateUsersWithLocationsDialog
        retailerNetworkUuid={retailerNetworkUuid}
        location={location}
        open={isAssociateExistingUsersWithLocationsDialogOpen}
        onClose={handleAssociateExistingUsersWithLocationsDialogOpen}
        onSubmit={onAssociateLocationsWithRetailerNetworkUsersDialogConfirm}
        loading={associateLocationsWithRetailerNetworkUsersInProgress}
      />

      {isAssociateNewUserWithLocationsDialogOpen && (
        <CreateAndAssociateNewUserWithLocationsDialog
          retailerNetworkUuid={retailerNetworkUuid}
          location={location}
          open={isAssociateNewUserWithLocationsDialogOpen}
          onClose={handleAssociateNewUserWithLocationsDialogOpen}
          onSubmit={handleCreateNewUserAndAssociateWithLocation}
        />
      )}
    </>
  );
};
